/**
 * Series: microsoft-365-local (epic #667)
 *
 * Pure data. No `@/` aliases or JSX so Node build scripts (sitemap, llms,
 * prerender manifest) can import it directly through scripts/lib/series.js.
 *
 * Posts are resolved by id from the blog data at render time. `plannedTitle`
 * is only a fallback for parts that are not published yet (shown unlinked as
 * "upcoming"); a published post always supplies its own title and excerpt.
 *
 * New composite scenario (anonymised Org S), deliberately NOT the Org A / Org B
 * shared-tenant case of #530/#557 — this is a single organisation's
 * build-vs-consume decision, not a two-party boundary.
 *
 * Microsoft 365 Local is a Microsoft product offering; this series is
 * independent EDUC4TE education, not endorsed by or affiliated with Microsoft.
 */
export default {
  slug: 'microsoft-365-local',
  publishDate: '2027-01-01',
  title: 'Microsoft 365 Local: sovereignty, security and operational readiness',
  shortTitle: 'Microsoft 365 Local',
  // 140 to 160 characters
  description:
    'An eight-part decision guide to Microsoft 365 Local: running Exchange, SharePoint and Skype for Business Server on your own Azure Local infrastructure.',
  intro:
    'Microsoft 365 Local lets an organisation run the Subscription Editions of Exchange Server, SharePoint Server and Skype for Business Server on its own Azure Local infrastructure, deployed through a certified partner and operated either cloud-connected or fully disconnected. It is the productivity pillar of Microsoft\'s Sovereign Private Cloud, and it is generally available, with disconnected operations announced in February 2026. This series is a decision guide, not a deployment: it works through one question in turn, which is when operating these workloads locally justifies the infrastructure, security and operational responsibility it adds. Every technical claim is traced to Microsoft documentation in a source ledger, and where the documentation is silent the series says so and labels the conclusion as our reading.',
  audience:
    'Enterprise architects, Microsoft platform owners, security and risk leaders, and organisations evaluating sovereign or disconnected Microsoft 365 environments.',
  whoItsFor: [
    'Enterprise architects scoping a sovereign or disconnected Microsoft productivity platform',
    'Microsoft platform owners deciding between Microsoft 365 cloud and Microsoft 365 Local',
    'Security and risk leaders who must re-establish controls locally that the cloud provided',
    'Organisations with data-residency, connectivity or sovereignty obligations that cloud alone cannot meet',
  ],
  whatYouWillLearn: [
    'Which workloads Microsoft 365 Local actually includes, and which cloud services are not documented as part of it',
    'When running productivity workloads locally is justified, and when Microsoft 365 cloud remains the better fit',
    'How the operational boundary changes between connected and disconnected mode, including the local control plane',
    'Why disconnected identity is OIDC and LDAP against your own directory, not Microsoft Entra ID running on-premises',
    'What the native backup does and does not protect, and why continuity must be rehearsed rather than assumed',
  ],
  // Published parts, in reading order (numeric here).
  parts: [
    { id: 155, plannedTitle: 'What is Microsoft 365 Local, and which workloads does it include?' },
    { id: 156, plannedTitle: 'Microsoft 365 Local vs Microsoft 365 Cloud: when does local make sense?' },
    { id: 157, plannedTitle: 'Connected or disconnected: designing the operational boundary' },
    { id: 158, plannedTitle: 'Identity and privileged access for Microsoft 365 Local' },
    { id: 159, plannedTitle: 'Security and governance: which controls must be re-established locally?' },
    { id: 160, plannedTitle: 'Patching, backup and recovery without cloud connectivity' },
    { id: 161, plannedTitle: 'Microsoft 365 Local costs, licensing and partner readiness' },
  ],
  // Part 8 is deliberately not published until a verified deployment supplies
  // evidence. It carries no id and has no MDX file.
  held: [
    {
      part: 8,
      title: 'Proving Microsoft 365 Local readiness: a pilot and acceptance test plan',
      status: 'held pending a verified deployment',
    },
  ],
  totalParts: 8,
  methodology: 'Independent decision guide based on Microsoft documentation, not a deployment. Anonymised: Org S.',
  methodologyDetail:
    'Every technical claim in this series is traced to Microsoft Learn documentation in a source ledger, and each post states the date that documentation was checked. The Azure Local disconnected-operations pages change quickly, so each claim is re-verified on the day its post is finalised. Where the documentation is silent or a capability is not listed, the series says so and labels the conclusion as our reading rather than a Microsoft statement. No organisation, tenant, sector or partner is identified, and no deployment or pilot results are reported until a pilot has actually run and been verified.',
  sourceLedgerNote:
    'Each post carries a Sources section naming the Microsoft documentation pages behind its claims, with the date each was checked. Where documentation is silent, a capability is described as not documented as included rather than as excluded. This series is independent education and is not endorsed by, affiliated with or certified by Microsoft; any framework or standard it references is a mapping, not assurance or certification.',
  faqs: [
    {
      question: 'What is Microsoft 365 Local?',
      answer:
        'Microsoft 365 Local runs the Subscription Editions of Exchange Server, SharePoint Server and Skype for Business Server on Azure Local infrastructure that the organisation owns and manages, deployed through a certified Microsoft partner. It is the productivity pillar of Microsoft\'s Sovereign Private Cloud and supports both cloud-connected and fully disconnected operation.',
    },
    {
      question: 'Is Microsoft 365 Local generally available or still in preview?',
      answer:
        'It is generally available, not preview. The disconnected-operations capability was announced in February 2026, and the product first reached general availability earlier. The series treats it as a shipping product and cites Microsoft documentation for each claim.',
    },
    {
      question: 'Does Microsoft 365 Local include Teams, Copilot, Purview or Intune?',
      answer:
        'Microsoft\'s documentation lists Exchange Server, SharePoint Server and Skype for Business Server as the workloads. Teams, Microsoft 365 Copilot, Purview and Intune are not listed as part of Microsoft 365 Local, and Foundry Local is a separate Sovereign Private Cloud pillar for AI. The series treats these as not documented as included and tells you to verify each capability individually, rather than assuming parity with the cloud.',
    },
    {
      question: 'In disconnected mode, does Microsoft Entra ID run on-premises?',
      answer:
        'No. In disconnected mode identity is handled locally against your own identity provider, using an OpenID Connect endpoint for authentication and an LDAP endpoint for groups, with a Root operator and Azure role-based access control applied to subscription scopes. Microsoft\'s documented example uses AD FS with Active Directory. Entra ID does not run locally, and the series corrects that common assumption.',
    },
    {
      question: 'What does the native backup protect?',
      answer:
        'The native disconnected-operations backup covers only the control-plane virtual machine data. It does not back up your Exchange or SharePoint workload data or the configured clusters, so workload data protection is a separate responsibility you must plan for. Restore is same-version only and depends on externally stored BitLocker recovery keys.',
    },
    {
      question: 'How much does Microsoft 365 Local cost?',
      answer:
        'There is no public price list. Microsoft 365 Local is quoted through Microsoft account teams and certified partners and depends on hardware scale, licensing and the support agreement. The series gives you the questions to ask and a bill-of-materials shape rather than any figure.',
    },
    {
      question: 'Has this design been deployed or tested?',
      answer:
        'No. This series is a decision guide based on Microsoft documentation. Part 8, which would report what a readiness pilot proved, is held until a verified deployment supplies evidence. Until then the series makes no claims about observed results.',
    },
    {
      question: 'Who is Org S?',
      answer:
        'Org S is an anonymised label for the single organisation in the worked decision. No tenant, client, sector, region or partner is identified.',
    },
  ],
  cta: {
    heading: 'Weighing a sovereign or disconnected Microsoft 365 deployment?',
    text: 'Talk to us about assessing whether Microsoft 365 Local suits your sovereignty obligations, your operating capacity and your risk appetite.',
    primary: { label: 'Explore our security assessments', href: '/assessments' },
    secondary: { label: 'Contact EDUC4TE', href: '/contact' },
  },
  // Date the series page content was last reviewed (ISO). Used for dateModified.
  lastReviewed: '2026-10-30',
};
